/**
 * Domínio WORKSPACE / DASHBOARD — schemas de `@imobdesk/contracts` (dashboard).
 * Mantém nomes de exportação usados pela UI.
 */
import { z } from 'zod'
import { formPayload } from '@imobdesk/form-utils'
import { dashboard, site } from '@imobdesk/contracts'

export const dashboardStatusSchema = dashboard.dashboardStatusSchema
export const dashboardSchema = dashboard.dashboardSchema
export const dashboardsResponseSchema = dashboard.dashboardsResponseSchema
export const setDashboardPrincipalSchema = dashboard.setDashboardPrincipalSchema
export const setDashboardPrincipalDataSchema =
  dashboard.setDashboardPrincipalDataSchema
export const setDashboardPrincipalResponseSchema =
  dashboard.setDashboardPrincipalResponseSchema
export const setDashboardPrincipalErrorSchema =
  dashboard.setDashboardPrincipalErrorSchema

export type Dashboard = z.infer<typeof dashboardSchema>
export type DashboardStatus = z.infer<typeof dashboardStatusSchema>
export type DashboardsResponse = z.infer<typeof dashboardsResponseSchema>
export type SetDashboardPrincipalRequest = z.infer<
  typeof setDashboardPrincipalSchema
>
export type SetDashboardPrincipalResponse = z.infer<
  typeof setDashboardPrincipalResponseSchema
>
export type SetDashboardPrincipalError = z.infer<
  typeof setDashboardPrincipalErrorSchema
>

export const personTypeSchema = dashboard.personTypeSchema
export const companyTypeSchema = dashboard.companyTypeSchema

export const dashboardDetailSchema = dashboard.dashboardDetailSchema.extend({
  trialExpiration: z
    .string()
    .nullable()
    .transform((val) => (val ? new Date(val) : null)),
  desbloqueioConfiancaAte: z
    .string()
    .nullable()
    .transform((val) => (val ? new Date(val) : null)),
  createdAt: z.string().transform((val) => new Date(val)),
  updatedAt: z.string().transform((val) => new Date(val)),
})
export const dashboardDetailResponseSchema =
  dashboard.dashboardDetailResponseSchema.extend({
    data: dashboardDetailSchema,
  })
export type DashboardDetail = z.infer<typeof dashboardDetailSchema>
export type DashboardDetailResponse = z.infer<
  typeof dashboardDetailResponseSchema
>

export const siteSchema = dashboard.siteSchema.extend({
  createdAt: z.string().transform((val) => new Date(val)),
  updatedAt: z.string().transform((val) => new Date(val)),
})
export const sitesResponseSchema = dashboard.sitesResponseSchema.extend({
  data: z.array(siteSchema),
})
export type Site = z.infer<typeof siteSchema>
export type SitesResponse = z.infer<typeof sitesResponseSchema>

export const editorLinkResponseSchema = dashboard.editorLinkResponseSchema
export type EditorLinkResponse = z.infer<typeof editorLinkResponseSchema>

export const createPortalStripeRequestSchema =
  dashboard.createPortalStripeRequestSchema
export const portalStripeDataSchema = dashboard.portalStripeDataSchema
export const portalStripeResponseSchema = dashboard.portalStripeResponseSchema
export type CreatePortalStripeRequest = z.infer<
  typeof createPortalStripeRequestSchema
>
export type PortalStripeResponse = z.infer<typeof portalStripeResponseSchema>

export const siteResponseSchema = dashboard.siteResponseSchema.extend({
  data: siteSchema,
})
export const updateSiteSchema = dashboard.updateSiteSchema
export type SiteResponse = z.infer<typeof siteResponseSchema>
export type UpdateSiteRequest = z.input<typeof updateSiteSchema>

export const dnsConfigRootSchema = dashboard.dnsConfigRootSchema
export const dnsConfigWwwSchema = dashboard.dnsConfigWwwSchema
export const dnsConfigVerificationSchema =
  dashboard.dnsConfigVerificationSchema
export const dnsConfigDnsSchema = dashboard.dnsConfigDnsSchema
export const dnsConfigDataSchema = dashboard.dnsConfigDataSchema
export const dnsConfigResponseSchema = dashboard.dnsConfigResponseSchema
export type DNSConfigResponse = z.infer<typeof dnsConfigResponseSchema>

export const artigoSchema = dashboard.artigoSchema.extend({
  createdAt: z.string().transform((str) => new Date(str)),
  updatedAt: z.string().transform((str) => new Date(str)),
})
export const artigosDataSchema = dashboard.artigosDataSchema.extend({
  items: z.array(artigoSchema),
})
export const artigosResponseSchema = dashboard.artigosResponseSchema.extend({
  data: artigosDataSchema,
})
export const artigoResponseSchema = dashboard.artigoResponseSchema.extend({
  data: artigoSchema,
})
export type Artigo = z.infer<typeof artigoSchema>
export type ArtigosData = z.infer<typeof artigosDataSchema>
export type ArtigosResponse = z.infer<typeof artigosResponseSchema>
export type ArtigoResponse = z.infer<typeof artigoResponseSchema>

export const tagSchema = dashboard.tagSchema
export const tagsResponseSchema = dashboard.tagsResponseSchema
export const tagDetailResponseSchema = dashboard.tagDetailResponseSchema
export const createTagRequestSchema = dashboard.createTagRequestSchema
export const createTagResponseSchema = dashboard.createTagResponseSchema
export const updateTagRequestSchema = dashboard.updateTagRequestSchema
export const updateTagResponseSchema = dashboard.updateTagResponseSchema
export const deleteTagResponseSchema = dashboard.deleteTagResponseSchema
export type Tag = z.infer<typeof tagSchema>
export type TagsResponse = z.infer<typeof tagsResponseSchema>
export type TagDetailResponse = z.infer<typeof tagDetailResponseSchema>
export type CreateTagRequest = z.input<typeof createTagRequestSchema>
export type CreateTagResponse = z.infer<typeof createTagResponseSchema>
export type UpdateTagRequest = z.input<typeof updateTagRequestSchema>
export type UpdateTagResponse = z.infer<typeof updateTagResponseSchema>
export type DeleteTagResponse = z.infer<typeof deleteTagResponseSchema>

export const createArtigoRequestSchema = dashboard.createArtigoRequestSchema
export const createArtigoResponseSchema =
  dashboard.createArtigoResponseSchema.extend({
    data: artigoSchema,
  })
export type CreateArtigoRequest = Omit<
  z.input<typeof createArtigoRequestSchema>,
  'slug'
> & { slug?: string }

export const updateArtigoRequestSchema = dashboard.updateArtigoRequestSchema
export const updateArtigoResponseSchema = createArtigoResponseSchema
export type CreateArtigoResponse = z.infer<typeof createArtigoResponseSchema>
export type UpdateArtigoRequest = z.input<typeof updateArtigoRequestSchema>
export type UpdateArtigoResponse = z.infer<typeof updateArtigoResponseSchema>

export const deleteArtigoResponseSchema = site.deleteArtigoResponseSchema
export type DeleteArtigoResponse = z.infer<typeof deleteArtigoResponseSchema>

export const generateImageUploadRequestSchema =
  dashboard.generateImageUploadRequestSchema
export const uploadUrlResponseSchema = dashboard.uploadUrlResponseSchema
export type GenerateImageUploadRequest = z.infer<
  typeof generateImageUploadRequestSchema
>
export type UploadUrlResponse = z.infer<typeof uploadUrlResponseSchema>

export const generateVideoUploadRequestSchema =
  site.generateSiteVideoUploadUrlSchema
export type GenerateVideoUploadRequest = z.infer<
  typeof generateVideoUploadRequestSchema
>

export const dashboardUsuarioSchema = dashboard.dashboardUsuarioSchema
export const dashboardUsuariosResponseSchema =
  dashboard.dashboardUsuariosResponseSchema
export type DashboardUsuario = z.infer<typeof dashboardUsuarioSchema>
export type DashboardUsuariosResponse = z.infer<
  typeof dashboardUsuariosResponseSchema
>

export const updateDashboardSchema = dashboard.updateDashboardRequestSchema
export const updateDashboardResponseSchema = dashboardDetailResponseSchema
export type UpdateDashboardRequest = z.input<typeof updateDashboardSchema>
export type UpdateDashboardResponse = z.infer<
  typeof updateDashboardResponseSchema
>

export const generateAssetUploadRequestSchema =
  dashboard.generateAssetUploadRequestSchema
export const assetUploadUrlResponseSchema =
  dashboard.assetUploadUrlResponseSchema
export type GenerateAssetUploadRequest = z.input<
  typeof generateAssetUploadRequestSchema
>
export type AssetUploadUrlResponse = z.infer<
  typeof assetUploadUrlResponseSchema
>

// ==================== Form: createPortalStripeFormSchema ====================

/** UX: validação de formulário — request API usa `createPortalStripeRequestSchema`. */
export const createPortalStripeFormSchema = createPortalStripeRequestSchema

export function mapCreatePortalStripeFormToApiRequest(input: CreatePortalStripeForm): CreatePortalStripeRequest {
  return createPortalStripeRequestSchema.parse(formPayload(input as Record<string, unknown>))
}

export type CreatePortalStripeForm = z.infer<typeof createPortalStripeFormSchema>

// ==================== Form: createTagFormSchema ====================

/** UX: validação de formulário — request API usa `createTagRequestSchema`. */
export const createTagFormSchema = createTagRequestSchema

export function mapCreateTagFormToApiRequest(input: CreateTagForm): CreateTagRequest {
  return createTagRequestSchema.parse(formPayload(input as Record<string, unknown>))
}

export type CreateTagForm = z.infer<typeof createTagFormSchema>

// ==================== Form: updateTagFormSchema ====================

/** UX: validação de formulário — request API usa `updateTagRequestSchema`. */
export const updateTagFormSchema = updateTagRequestSchema

export function mapUpdateTagFormToApiRequest(input: UpdateTagForm): UpdateTagRequest {
  return updateTagRequestSchema.parse(formPayload(input as Record<string, unknown>))
}

export type UpdateTagForm = z.infer<typeof updateTagFormSchema>

// ==================== Form: createArtigoFormSchema ====================

/** UX: validação de formulário — request API usa `createArtigoRequestSchema`. */
export const createArtigoFormSchema = createArtigoRequestSchema

export function mapCreateArtigoFormToApiRequest(input: CreateArtigoForm): CreateArtigoRequest {
  return createArtigoRequestSchema.parse(formPayload(input as Record<string, unknown>))
}

export type CreateArtigoForm = CreateArtigoRequest

// ==================== Form: updateArtigoFormSchema ====================

/** UX: validação de formulário — request API usa `updateArtigoRequestSchema`. */
export const updateArtigoFormSchema = updateArtigoRequestSchema

export function mapUpdateArtigoFormToApiRequest(input: UpdateArtigoForm): UpdateArtigoRequest {
  return updateArtigoRequestSchema.parse(formPayload(input as Record<string, unknown>))
}

export type UpdateArtigoForm = UpdateArtigoRequest

// ==================== Form: updateSiteFormSchema ====================

/** UX: validação de formulário — request API usa `updateSiteSchema`. */
export const updateSiteFormSchema = updateSiteSchema

export function mapUpdateSiteFormToApiRequest(
  input: UpdateSiteForm,
): UpdateSiteRequest {
  return updateSiteSchema.parse(formPayload(input as Record<string, unknown>))
}

export type UpdateSiteForm = z.infer<typeof updateSiteFormSchema>

// ==================== Form: updateDashboardFormSchema ====================

/** UX: remove strings vazias antes do parse do contrato. */
export const updateDashboardFormSchema = updateDashboardSchema

export function mapUpdateDashboardFormToApiRequest(
  input: UpdateDashboardForm,
): UpdateDashboardRequest {
  const cleaned = Object.fromEntries(
    Object.entries(input as Record<string, unknown>).filter(([_, value]) => {
      if (value === null || value === undefined) return false
      if (typeof value === 'string' && value.trim() === '') return false
      return true
    }),
  )
  return updateDashboardSchema.parse(cleaned)
}

export type UpdateDashboardForm = z.infer<typeof updateDashboardFormSchema>

// ==================== Form: generateImageUploadFormSchema ====================

export const generateImageUploadFormSchema = generateImageUploadRequestSchema

export function mapGenerateImageUploadFormToApiRequest(
  input: GenerateImageUploadForm,
): GenerateImageUploadRequest {
  return generateImageUploadRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export type GenerateImageUploadForm = z.infer<
  typeof generateImageUploadFormSchema
>

// ==================== Form: generateVideoUploadFormSchema ====================

export const generateVideoUploadFormSchema = generateVideoUploadRequestSchema

export function mapGenerateVideoUploadFormToApiRequest(
  input: GenerateVideoUploadForm,
): GenerateVideoUploadRequest {
  return generateVideoUploadRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export type GenerateVideoUploadForm = z.infer<
  typeof generateVideoUploadFormSchema
>

// ==================== Form: generateAssetUploadFormSchema ====================

export const generateAssetUploadFormSchema = generateAssetUploadRequestSchema

export function mapGenerateAssetUploadFormToApiRequest(
  input: GenerateAssetUploadForm,
): GenerateAssetUploadRequest {
  return generateAssetUploadRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export type GenerateAssetUploadForm = z.input<
  typeof generateAssetUploadFormSchema
>

// ==================== Form: dashboardWatermarkFormSchema ====================

const watermarkPositionEnum = z.enum([
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
  'center',
])

/** UX: configuração visual da marca d'água (proportion → watermarkScalePercent na API). */
export const dashboardWatermarkFormSchema = z.object({
  opacity: z.number().min(0).max(1),
  position: watermarkPositionEnum,
  proportion: z.number().min(0.05).max(1),
  margin: z.number().int().min(0).max(100),
})

export function mapDashboardWatermarkFormToApiRequest(
  input: DashboardWatermarkForm,
): UpdateDashboardRequest {
  const ratioPercent = Math.round(
    Math.min(1, Math.max(0.05, Number(input.proportion) || 0.25)) * 100,
  )
  return updateDashboardSchema.parse({
    watermarkScalePercent: ratioPercent,
    opacity: input.opacity,
    position: input.position,
    margin: Math.max(0, Math.round(Number(input.margin) || 0)),
  })
}

export type DashboardWatermarkForm = z.infer<typeof dashboardWatermarkFormSchema>

// ==================== Form: dashboardInformacoesBasicasFormSchema ====================

/** UX: card de informações básicas do dashboard. */
export const dashboardInformacoesBasicasFormSchema = z.object({
  titulo: z.string(),
  email: z.union([z.literal(''), z.string().email('Email inválido')]),
  telefone: z.string(),
  mobilePhone: z.string(),
})

export function mapDashboardInformacoesBasicasFormToApiRequest(
  input: DashboardInformacoesBasicasForm,
): UpdateDashboardRequest {
  return mapUpdateDashboardFormToApiRequest(input)
}

export type DashboardInformacoesBasicasForm = z.infer<
  typeof dashboardInformacoesBasicasFormSchema
>

// ==================== Form: dashboardEnderecoFormSchema ====================

/** UX: card de endereço do dashboard. */
export const dashboardEnderecoFormSchema = z.object({
  cep: z.string(),
  uf: z.string(),
  cidade: z.string(),
  bairro: z.string(),
  endereco: z.string(),
  numero: z.string(),
  complemento: z.string(),
})

export function mapDashboardEnderecoFormToApiRequest(
  input: DashboardEnderecoForm,
): UpdateDashboardRequest {
  return mapUpdateDashboardFormToApiRequest(input)
}

export type DashboardEnderecoForm = z.infer<typeof dashboardEnderecoFormSchema>

// ==================== Form: dashboardDadosComerciaisFormSchema ====================

const dashboardCompanyTypeFormEnum = z.enum([
  'MEI',
  'LIMITED',
  'INDIVIDUAL',
  'ASSOCIATION',
])

/** UX: card de dados comerciais — aceita vazio em personType/companyType. */
export const dashboardDadosComerciaisFormSchema = z.object({
  razaoSocial: z.string(),
  cpfCnpj: z.string(),
  personType: z.union([z.literal(''), personTypeSchema]),
  companyType: z.union([z.literal(''), dashboardCompanyTypeFormEnum]),
})

export function mapDashboardDadosComerciaisFormToApiRequest(
  input: DashboardDadosComerciaisForm,
): UpdateDashboardRequest {
  return mapUpdateDashboardFormToApiRequest({
    razaoSocial: input.razaoSocial || undefined,
    cpfCnpj: input.cpfCnpj || undefined,
    personType: input.personType || undefined,
    companyType: input.companyType || undefined,
  })
}

export type DashboardDadosComerciaisForm = z.infer<
  typeof dashboardDadosComerciaisFormSchema
>

export function mapDashboardDadosPageFormsToApiRequest(input: {
  basicas: DashboardInformacoesBasicasForm
  endereco: DashboardEnderecoForm
  comerciais: DashboardDadosComerciaisForm
}): UpdateDashboardRequest {
  return mapUpdateDashboardFormToApiRequest({
    ...input.basicas,
    ...input.endereco,
    razaoSocial: input.comerciais.razaoSocial || undefined,
    cpfCnpj: input.comerciais.cpfCnpj || undefined,
    personType: input.comerciais.personType || undefined,
    companyType: input.comerciais.companyType || undefined,
  })
}

// ==================== Form: siteGeralFormSchema ====================

/** UX: seção geral do site (nome, CRECI, publicação). */
export const siteGeralFormSchema = z.object({
  nome: z.string().min(1, 'Nome do site é obrigatório'),
  creci: z.string(),
  publicado: z.boolean(),
})

export function mapSiteGeralFormToApiRequest(
  input: SiteGeralForm,
): UpdateSiteRequest {
  return updateSiteSchema.parse({
    nome: input.nome,
    creci: input.creci.trim() || null,
    publicado: input.publicado,
  })
}

export type SiteGeralForm = z.infer<typeof siteGeralFormSchema>

// ==================== Form: siteDominiosFormSchema ====================

/** UX: domínio temporário e próprio com validação de formato. */
export const siteDominiosFormSchema = z.object({
  dominioDev: z
    .string()
    .refine((v) => v === '' || /^[a-z0-9-]+$/.test(v), {
      message: 'Apenas letras minúsculas, números e hífens são permitidos',
    })
    .refine((v) => v === '' || (!v.startsWith('-') && !v.endsWith('-')), {
      message: 'Não pode começar ou terminar com hífen',
    })
    .refine((v) => v === '' || v.length >= 3, {
      message: 'Mínimo de 3 caracteres',
    })
    .refine((v) => v === '' || v.length <= 63, {
      message: 'Máximo de 63 caracteres',
    }),
  dominioProprio: z
    .string()
    .refine(
      (v) => {
        if (v === '') return true
        const domainRegex =
          /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i
        return domainRegex.test(v)
      },
      { message: 'Formato de domínio inválido (ex: exemplo.com.br)' },
    )
    .refine(
      (v) =>
        v === '' ||
        (!v.startsWith('.') &&
          !v.endsWith('.') &&
          !v.startsWith('-') &&
          !v.endsWith('-')),
      { message: 'Domínio inválido' },
    )
    .refine((v) => v === '' || v.length <= 253, {
      message: 'Domínio muito longo (máximo 253 caracteres)',
    }),
})

export function mapSiteDominiosFormToApiRequest(
  input: SiteDominiosForm,
): UpdateSiteRequest {
  return updateSiteSchema.parse({
    dominioDev: input.dominioDev.trim() || undefined,
    dominioProprio: input.dominioProprio.trim() || null,
  })
}

export type SiteDominiosForm = z.infer<typeof siteDominiosFormSchema>
